import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AdminOfferControls } from '../components/offers';
import { 
  Users, Car, Calendar, Euro, Phone, Mail, MapPin, 
  Filter, ChevronDown, ChevronUp, Edit, RefreshCw,
  Briefcase, Building, Clock, AlertTriangle, MoreVertical
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PortafoglioClientiPage = () => {
  const { user, token, getImpersonateParams, impersonatedUser } = useAuth();
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agenti, setAgenti] = useState([]);
  const [expandedCliente, setExpandedCliente] = useState(null);
  const [editDialog, setEditDialog] = useState({ open: false, contratto: null });
  const [filters, setFilters] = useState({
    agente_id: '',
    scadenza_da: '',
    scadenza_a: '',
    canone_min: '',
    canone_max: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const isAdmin = ['admin', 'super_admin', 'top_agent'].includes(user?.role);
  const isClienteSmartRole = user?.role === 'cliente_smart';

  const fetchClienti = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // Passa view_as se Admin sta impersonando un utente
      const impersonateParams = getImpersonateParams();
      if (impersonateParams.view_as) {
        params.append('view_as', impersonateParams.view_as);
      }
      
      if (filters.agente_id) params.append('agente_id', filters.agente_id);
      if (filters.scadenza_da) params.append('scadenza_da', filters.scadenza_da);
      if (filters.scadenza_a) params.append('scadenza_a', filters.scadenza_a);
      if (filters.canone_min) params.append('canone_min', filters.canone_min);
      if (filters.canone_max) params.append('canone_max', filters.canone_max);

      const response = await fetch(`${API}/api/portafoglio-clienti?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setClienti(data.clienti || []);
      }
    } catch (error) {
      console.error('Error fetching clienti:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filters, getImpersonateParams]);

  const fetchAgenti = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/agenti-list`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAgenti(data.agenti || []);
      }
    } catch (error) {
      console.error('Error fetching agenti:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchClienti();
    if (isAdmin) {
      fetchAgenti();
    }
  }, [fetchClienti, fetchAgenti, isAdmin]);

  const handleUpdateContratto = async () => {
    if (!editDialog.contratto) return;
    
    try {
      const response = await fetch(`${API}/api/contratti/${editDialog.contratto.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          agente_id: editDialog.newAgenteId || undefined,
          canone_mensile: editDialog.newCanone ? parseFloat(editDialog.newCanone) : undefined,
          data_scadenza: editDialog.newScadenza || undefined
        })
      });
      
      if (response.ok) {
        setEditDialog({ open: false, contratto: null });
        fetchClienti();
      }
    } catch (error) {
      console.error('Error updating contratto:', error);
    }
  };

  const getDaysUntilExpiry = (dateStr) => {
    if (!dateStr) return null;
    const today = new Date();
    const expiry = new Date(dateStr);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryBadge = (dateStr) => {
    const days = getDaysUntilExpiry(dateStr);
    if (days === null) return null;
    
    if (days < 0) {
      return <Badge className="bg-red-100 text-red-700">Scaduto</Badge>;
    } else if (days <= 150) { // ~5 mesi
      return <Badge className="bg-amber-100 text-amber-700">Scade tra {days} giorni</Badge>;
    } else {
      return <Badge className="bg-emerald-100 text-emerald-700">Attivo</Badge>;
    }
  };

  const clearFilters = () => {
    setFilters({
      agente_id: '',
      scadenza_da: '',
      scadenza_a: '',
      canone_min: '',
      canone_max: ''
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
            <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {isClienteSmartRole ? 'I Miei Contratti' : 'Portafoglio Clienti'}
            </h1>
            <p className="text-slate-500 mt-1">
              {isClienteSmartRole 
                ? 'Visualizza i tuoi contratti attivi' 
                : 'Gestisci i tuoi clienti attivi e i loro contratti'
              }
            </p>
          </div>
          {/* Filtri solo per Admin/Agente */}
          {!isClienteSmartRole && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                Filtri
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            <Button onClick={fetchClienti} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Aggiorna
            </Button>
          </div>
          )}
        </div>

        {/* Filters - solo per Admin/Agente */}
        {!isClienteSmartRole && showFilters && (
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {isAdmin && (
                  <div className="space-y-2">
                    <Label>Agente</Label>
                    <Select
                      value={filters.agente_id || 'all'}
                      onValueChange={(value) => setFilters({ ...filters, agente_id: value === 'all' ? '' : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Tutti gli agenti" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutti gli agenti</SelectItem>
                        {agenti.map(a => (
                          <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Scadenza da</Label>
                  <Input
                    type="date"
                    value={filters.scadenza_da}
                    onChange={(e) => setFilters({ ...filters, scadenza_da: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Scadenza a</Label>
                  <Input
                    type="date"
                    value={filters.scadenza_a}
                    onChange={(e) => setFilters({ ...filters, scadenza_a: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Canone min (€)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={filters.canone_min}
                    onChange={(e) => setFilters({ ...filters, canone_min: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Canone max (€)</Label>
                  <Input
                    type="number"
                    placeholder="∞"
                    value={filters.canone_max}
                    onChange={(e) => setFilters({ ...filters, canone_max: e.target.value })}
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button variant="ghost" onClick={clearFilters}>
                  Pulisci filtri
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Clienti Attivi</p>
                  <p className="text-2xl font-bold text-slate-800">{clienti.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Car className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Contratti Totali</p>
                  <p className="text-2xl font-bold text-slate-800">
                    {clienti.reduce((acc, c) => acc + (c.contratti?.length || 0), 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Euro className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Volume Mensile</p>
                  <p className="text-2xl font-bold text-slate-800">
                    €{clienti.reduce((acc, c) => acc + c.contratti?.reduce((a, ct) => a + (ct.canone_mensile || 0), 0), 0).toLocaleString('it-IT')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Clienti List */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Caricamento...</div>
        ) : clienti.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nessun cliente attivo trovato</p>
              <p className="text-sm text-slate-400 mt-1">
                I clienti appariranno qui quando un ordine viene completato
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {clienti.map(({ contact, contratti }) => (
              <Card key={contact.id} className="overflow-hidden">
                <CardHeader 
                  className="cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpandedCliente(expandedCliente === contact.id ? null : contact.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                        {contact.nome_cognome?.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <CardTitle className="text-lg" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                          {contact.nome_cognome}
                        </CardTitle>
                        <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {contact.telefono}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {contact.email}
                          </span>
                          {contact.citta && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {contact.citta}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <Badge className="bg-blue-100 text-blue-700 mb-1">
                          {contratti?.length || 0} contratt{contratti?.length === 1 ? 'o' : 'i'}
                        </Badge>
                        {contact.scadenza_piu_vicina && (
                          <div className="mt-1">
                            {getExpiryBadge(contact.scadenza_piu_vicina)}
                          </div>
                        )}
                      </div>
                      {expandedCliente === contact.id ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                {expandedCliente === contact.id && (
                  <CardContent className="border-t bg-slate-50/50">
                    <div className="space-y-4 pt-4">
                      {/* Info Cliente */}
                      {contact.tipologia_cliente && (
                        <div className="p-4 bg-white rounded-lg border">
                          <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                            <Building className="w-4 h-4" />
                            Dati Aziendali
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500">Tipologia</p>
                              <p className="font-medium">{contact.tipologia_cliente?.replace(/_/g, ' ')}</p>
                            </div>
                            {contact.partita_iva && (
                              <div>
                                <p className="text-slate-500">P.IVA</p>
                                <p className="font-medium">{contact.partita_iva}</p>
                              </div>
                            )}
                            {contact.codice_fiscale && (
                              <div>
                                <p className="text-slate-500">Codice Fiscale</p>
                                <p className="font-medium">{contact.codice_fiscale}</p>
                              </div>
                            )}
                            {contact.indirizzo && (
                              <div>
                                <p className="text-slate-500">Indirizzo</p>
                                <p className="font-medium">{contact.indirizzo}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Contratti */}
                      <h4 className="font-medium text-slate-700 flex items-center gap-2">
                        <Car className="w-4 h-4" />
                        Contratti Attivi
                      </h4>
                      <div className="space-y-3">
                        {contratti?.map(contratto => (
                          <div 
                            key={contratto.id}
                            className="p-4 bg-white rounded-lg border hover:border-blue-300 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  {/* Codice Offerta Univoco */}
                                  {contratto.codice_offerta && (
                                    <span className="text-xs font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                                      {contratto.codice_offerta}
                                    </span>
                                  )}
                                  <p className="font-semibold text-slate-800">
                                    {contratto.vehicle_info?.marca} {contratto.vehicle_info?.modello}
                                  </p>
                                  {getExpiryBadge(contratto.data_scadenza)}
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                                  <div>
                                    <p className="text-slate-500">Canone</p>
                                    <p className="font-bold text-emerald-600">
                                      €{contratto.canone_mensile?.toLocaleString('it-IT')}/mese
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-slate-500">Durata</p>
                                    <p className="font-medium">{contratto.durata_mesi} mesi</p>
                                  </div>
                                  <div>
                                    <p className="text-slate-500">Km/Anno</p>
                                    <p className="font-medium">{contratto.km_annui?.toLocaleString('it-IT')}</p>
                                  </div>
                                  <div>
                                    <p className="text-slate-500">Inizio</p>
                                    <p className="font-medium">
                                      {contratto.data_inizio_contratto ? new Date(contratto.data_inizio_contratto).toLocaleDateString('it-IT') : '-'}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-slate-500">Scadenza</p>
                                    <p className="font-medium">
                                      {contratto.data_scadenza ? new Date(contratto.data_scadenza).toLocaleDateString('it-IT') : '-'}
                                    </p>
                                  </div>
                                </div>
                                {isAdmin && (
                                  <div className="mt-2 text-sm text-slate-500 flex items-center gap-1">
                                    <Briefcase className="w-3 h-3" />
                                    Agente: {contratto.agente_name}
                                  </div>
                                )}
                              </div>
                              {/* Admin Controls - Edit + Annulla/Rollback */}
                              {isAdmin && (
                                <div className="flex items-center gap-1">
                                  {/* Menu Admin per Annullamento/Rollback */}
                                  {contratto.offer_id && (
                                    <AdminOfferControls 
                                      offer={{
                                        id: contratto.offer_id,
                                        status: 'contratto',
                                        esito: 'contratto_firmato',
                                        contratto_id: contratto.id,
                                        contact_id: contact.id
                                      }} 
                                      onUpdate={fetchClienti} 
                                    />
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditDialog({ 
                                        open: true, 
                                        contratto,
                                        newAgenteId: contratto.agente_id,
                                        newCanone: contratto.canone_mensile?.toString(),
                                        newScadenza: contratto.data_scadenza
                                      });
                                    }}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Edit Contratto Dialog */}
        <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Modifica Contratto
              </DialogTitle>
            </DialogHeader>
            {editDialog.contratto && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Veicolo</p>
                  <p className="font-medium">
                    {editDialog.contratto.vehicle_info?.marca} {editDialog.contratto.vehicle_info?.modello}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Agente Assegnato</Label>
                  <Select
                    value={editDialog.newAgenteId || ''}
                    onValueChange={(value) => setEditDialog({ ...editDialog, newAgenteId: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona agente" />
                    </SelectTrigger>
                    <SelectContent>
                      {agenti.map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Canone Mensile (€)</Label>
                  <Input
                    type="number"
                    value={editDialog.newCanone || ''}
                    onChange={(e) => setEditDialog({ ...editDialog, newCanone: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Scadenza Contratto</Label>
                  <Input
                    type="date"
                    value={editDialog.newScadenza || ''}
                    onChange={(e) => setEditDialog({ ...editDialog, newScadenza: e.target.value })}
                  />
                  <p className="text-xs text-slate-500">
                    Modifica la data in caso di proroghe del contratto
                  </p>
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setEditDialog({ open: false, contratto: null })}>
                    Annulla
                  </Button>
                  <Button onClick={handleUpdateContratto}>
                    Salva Modifiche
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
  );
};

export default PortafoglioClientiPage;
